// ValidationOverrideCheckbox — the control half of validationOverride.js.
//
// Renders beside a field's LABEL (not under the input) because it changes what
// the field means, not what it holds: "this value is allowed to break its
// limit" belongs with the name of the thing, the way a qualifier does.
//
// Turning the limit OFF asks first — that direction removes a safeguard, so it
// is the one that deserves a sentence explaining what is being given up.
// Turning it back ON is silent: restoring a check needs no permission.
//
// Both directions re-validate the field immediately, so the error appears or
// clears the moment the box is used, rather than at the next submit.
import { Checkbox, Form, Tooltip } from 'antd';
import { openFormDecision } from './formDecisionDialog';
import { overrideConfig, overrideConfirmCopy } from './validationOverride';

export default function ValidationOverrideCheckbox({ form, field, flagPath, fieldPath, disabled }) {
  const cfg = overrideConfig(field);
  const checked = Form.useWatch(flagPath, form);
  if (!cfg) return null;
  const label = cfg.label || 'Allow beyond the limit';

  const revalidate = () => {
    // Deferred: antd must see the new flag value before the rules re-run, and
    // the rules read it through getFieldValue at validation time.
    setTimeout(() => {
      form?.validateFields?.([fieldPath]).catch(() => { });
    });
  };

  const onChange = async (event) => {
    const next = event.target.checked;
    if (!next) {
      form.setFieldValue(flagPath, false);
      revalidate();
      return;
    }
    const confirmed = await openFormDecision(overrideConfirmCopy(field));
    // A declined confirmation must leave the box exactly as it was. antd has
    // already painted it checked by this point, so the value is written back
    // explicitly rather than simply not-set.
    form.setFieldValue(flagPath, Boolean(confirmed));
    revalidate();
  };

  // Label on HOVER, not inline. A rate field's label line is already carrying a
  // name, a required mark and (on a combined block) three inputs beside it; a
  // full sentence wedged in there crowds the one thing the user came to read.
  // The box stays visible because the OPTION has to be discoverable — only its
  // explanation is deferred to hover/focus, where someone who wants it will
  // look. The same text is the accessible name, so it is never hover-only for
  // a screen reader or a keyboard user.
  const title = cfg.help ? `${label} — ${cfg.help}` : label;

  return (
    <Tooltip title={title}>
      <span className="v1-override-wrap">
        <Checkbox
          className="v1-override-checkbox"
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={onChange}
          aria-label={title}
        />
      </span>
    </Tooltip>
  );
}
